import React from "react";
import { Card } from "@/components/ui/card";
import MainLayout from "@/components/layouts/MainLayout";
import { useAvailableScreens } from "@/hooks/useAvailableScreens";
import { useData } from "@/contexts/DataContext";
import { useAdManagement } from "@/hooks/useAdManagement";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

// Import the components
import AdSearch from "@/components/ads/AdSearch";
import AdList from "@/components/ads/AdList";
import EditAdDialog from "@/components/ads/EditAdDialog";
import EditMaterialDialog from "@/components/ads/EditMaterialDialog";
import DeleteAdDialog from "@/components/ads/DeleteAdDialog";
import AdsHeader from "@/components/ads/AdsHeader";

const Ads = () => {
  const { screens, ads, expiredAds } = useData();
  const { getAvailableScreensForEdit, getAllScreens } = useAvailableScreens(screens);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const {
    searchName,
    setSearchName,
    searchDate, 
    setSearchDate,
    isSearching,
    handleSearch,
    clearSearch,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isMaterialDialogOpen,
    setIsMaterialDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    selectedAd,
    isDeleting,
    openEditDialog,
    openMaterialDialog,
    openDeleteDialog,
    handleEditAd,
    handleDeleteAd,
    handleUpdateMaterialUrl,
    canEditOrDeleteAd,
  } = useAdManagement();

  // Combinar anuncios activos y vencidos sin duplicados
  const allAds = React.useMemo(() => {
    // Crear un Map para mantener un registro de los anuncios ya agregados
    const uniqueAds = new Map();
    
    // Primero agregar los anuncios activos
    ads.forEach(ad => {
      if (!ad.isExpired) {
        uniqueAds.set(ad.id, ad);
      }
    });
    
    // Luego agregar los anuncios vencidos que no estén en la lista activa
    expiredAds.forEach(ad => {
      if (!uniqueAds.has(ad.id)) {
        uniqueAds.set(ad.id, ad);
      }
    });
    
    // Convertir el Map a un array y ordenar por fecha
    return Array.from(uniqueAds.values()).sort((a, b) => {
      const dateA = new Date(a.endDate);
      const dateB = new Date(b.endDate);
      return dateB.getTime() - dateA.getTime();
    });
  }, [ads, expiredAds]);

  // Función para redirigir a facturación
  const openAddDialog = () => {
    navigate('/facturacion');
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <AdsHeader openAddDialog={openAddDialog} />

        <AdSearch 
          searchName={searchName}
          setSearchName={setSearchName}
          searchDate={searchDate}
          setSearchDate={setSearchDate}
          handleSearch={handleSearch}
          clearSearch={clearSearch}
          isSearching={isSearching}
        />

        <Card>
          <AdList 
            ads={allAds}
            openEditDialog={openEditDialog}
            openDeleteDialog={openDeleteDialog}
            openAddDialog={openAddDialog}
            canEditOrDeleteAd={canEditOrDeleteAd}
          />
        </Card>
      </div>

      <EditAdDialog 
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSubmit={handleEditAd}
        availableScreens={selectedAd ? getAvailableScreensForEdit(selectedAd) : []}
        selectedAd={selectedAd}
      />

      <EditMaterialDialog 
        isOpen={isMaterialDialogOpen}
        onOpenChange={setIsMaterialDialogOpen}
        onSubmit={handleUpdateMaterialUrl}
        selectedAd={selectedAd}
      />

      <DeleteAdDialog 
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDeleteAd}
        selectedAd={selectedAd}
        isDeleting={isDeleting}
      />
    </MainLayout>
  );
};

export default Ads;
